import { useState, useEffect, useCallback, useRef, forwardRef, useImperativeHandle } from 'react';
import ScannerConfigModal from '../components/ScannerConfigModal';
import {
  Search,
  RefreshCw,
  ExternalLink,
  Package,
  Clock,
  Eye,
  Zap,
  Target,
  Trophy
} from 'lucide-react';

const API = 'https://app.24review.de';
const token = () => localStorage.getItem('token');

const QUEUE_LABELS = {
  potluck: { label: 'PV', color: '#1565c0', bg: '#1b3a5e' },
  all_items: { label: 'AI', color: '#2e7d32', bg: '#1b3a1b' },
};

function QueueBadge({ queue, product }) {
  let q = QUEUE_LABELS[queue] || { label: queue || '—', color: '#555', bg: '#333' };

  if (queue === 'potluck') {
    const account = String(product?.scanner_account_name || '').trim();
    const accountNo = account.match(/\d+/)?.[0];

    if (product?.pv_scope === 'global') {
      q = { label: accountNo ? `PV Global ${accountNo}` : 'PV Global', color: '#c4b5fd', bg: '#3b216f' };
    } else if (accountNo) {
      q = { label: `PV ${accountNo}`, color: '#90caf9', bg: '#1b3a5e' };
    } else {
      q = { label: 'PV unbekannt', color: '#facc15', bg: '#3f3512' };
    }
  }

  return (
    <span
      title={queue === 'potluck' ? (product?.scanner_account_name || product?.pv_scope || 'PV ohne Account-Zuordnung') : q.label}
      style={{
        background: q.bg,
        color: q.color,
        padding: '2px 8px',
        borderRadius: 10,
        fontSize: 11,
        fontWeight: 'bold',
        whiteSpace: 'nowrap',
      }}
    >
      {q.label}
    </span>
  );
}

const ScannerStatsPanel = forwardRef(function ScannerStatsPanel(props, ref) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showConfigModal, setShowConfigModal] = useState(false);

  const fetchStats = async () => {
    setLoading(true);
    setError(null);

    try {
      const res = await fetch(`${API}/stats/scanner`, {
        headers: { Authorization: `Bearer ${token()}` },
      });

      const data = await res.json();

      if (data.ok) setStats(data.stats);
      else setError('Fehler beim Laden');
    } catch (e) {
      setError('Verbindungsfehler');
    }

    setLoading(false);
  };

  useImperativeHandle(ref, () => ({
    refresh: fetchStats,
  }));

  useEffect(() => {
    fetchStats();
  }, []);

  const fmt = (n) => (n || 0).toLocaleString('de-DE');

  if (loading) {
    return (
      <div style={ss.panel}>
        <div style={{ color: 'var(--text3)', fontSize: 12, padding: '12px 16px' }}>
          ⏳ Lade Scanner-Statistiken...
        </div>
      </div>
    );
  }

  if (error || !stats) {
    return (
      <div style={ss.panel}>
        <div style={{ color: 'var(--text3)', fontSize: 12, padding: '12px 16px' }}>
          {error || 'Keine Daten'} —{' '}
          <span style={{ color: 'var(--accent)', cursor: 'pointer' }} onClick={fetchStats}>
            Erneut versuchen
          </span>
        </div>
      </div>
    );
  }

  const todayAI = stats.today?.ai ?? stats.today?.za ?? 0;
  const todayPV = stats.today?.pv ?? 0;
  const totalAI = stats.byQueue?.ai ?? stats.byQueue?.za ?? 0;
  const totalPV = stats.byQueue?.pv ?? 0;
  const allUsersTotalAI = stats.today?.allUsersTotalAI ?? stats.today?.allUsersTotal ?? 0;

  const coveragePct = Math.min(stats.today?.coveragePercent || 0, 100);
  const rankProgress = Math.min(stats.rank?.progress || 0, 100);

  return (
    <div style={ss.panel}>
      <div style={ss.panelHeader}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Zap size={15} color="var(--accent)" />
          <span style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: 14 }}>
            Scanner Statistiken
          </span>

          {stats.activeSession && (
            <span
              style={{
                background: '#1b5e20',
                color: '#a5d6a7',
                fontSize: 10,
                padding: '2px 8px',
                borderRadius: 10,
                fontWeight: 600,
              }}
            >
              🟢 AKTIV
            </span>
          )}
        </div>

        <div style={{ display: 'flex', gap: 6 }}>
          <button style={ss.refreshBtn} onClick={() => setShowConfigModal(true)} title="Scanner Einstellungen">
            ⚙️
          </button>
        </div>
      </div>

      <div style={ss.body}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
          <div style={ss.infoBox}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 6 }}>
              <Trophy size={12} color="#e94560" />
              <span style={ss.infoLabel}>Rang</span>
            </div>

            <div style={ss.infoValue}>{stats.rank?.name || '—'}</div>

            <div style={ss.barWrap}>
              <div
                style={{
                  ...ss.barFill,
                  width: `${rankProgress}%`,
                  background: 'linear-gradient(90deg, #e94560, #ff8a65)',
                }}
              />
            </div>

            <div style={ss.infoFooter}>
              <span>{rankProgress}%</span>
              {stats.rank?.nextName ? (
                <span>noch {fmt(stats.rank.remaining)}</span>
              ) : (
                <span>👑 Max!</span>
              )}
            </div>
          </div>

          <div style={ss.infoBox}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 6 }}>
              <Target size={12} color="#4CAF50" />
              <span style={ss.infoLabel}>Heute gescannt</span>
            </div>

            <div style={ss.coverageValue}>{coveragePct}%</div>

            <div style={ss.barWrap}>
              <div
                style={{
                  ...ss.barFill,
                  width: `${coveragePct}%`,
                  background: 'linear-gradient(90deg, #1b5e20, #66bb6a)',
                }}
              />
            </div>

            <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 3 }}>
              {fmt(todayAI)} / {fmt(allUsersTotalAI)} AI heute
            </div>
          </div>
        </div>

        <div style={ss.grid}>
          <StatTile label="Heute AI" value={fmt(todayAI)} sub="neue Artikel" color="#e94560" />
          <StatTile label="Heute PV" value={fmt(todayPV)} sub="priv. Vorschläge" color="#90caf9" />
          <StatTile label="Gesamt" value={fmt(stats.totalProducts)} sub="unique ASINs" color="#a5d6a7" />
          <StatTile label="AI gesamt" value={fmt(totalAI)} sub="Alle Artikel" />
          <StatTile label="PV gesamt" value={fmt(totalPV)} sub="Priv. Vorschläge" color="#90caf9" />
        </div>
      </div>

      {showConfigModal && (
        <ScannerConfigModal
          isOpen={showConfigModal}
          onClose={() => setShowConfigModal(false)}
          onSave={() => {
            setShowConfigModal(false);
            fetchStats();
          }}
        />
      )}
    </div>
  );
});

function StatTile({ label, value, sub, color }) {
  return (
    <div style={ss.tile}>
      <div style={ss.tileLabel}>{label}</div>
      <div style={{ ...ss.tileVal, color: color || 'var(--text)' }}>{value}</div>
      <div style={ss.tileSub}>{sub}</div>
    </div>
  );
}

const ss = {
  panel: {
    background: 'var(--bg2)',
    border: '1px solid var(--border)',
    borderRadius: 12,
    marginBottom: 20,
    overflow: 'hidden',
  },
  panelHeader: {
    padding: '12px 16px',
    borderBottom: '1px solid var(--border)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    background: 'var(--bg3)',
  },
  refreshBtn: {
    background: 'var(--bg2)',
    border: '1px solid var(--border)',
    borderRadius: 6,
    padding: '4px 8px',
    cursor: 'pointer',
    color: 'var(--text3)',
    display: 'flex',
    alignItems: 'center',
  },
  body: { padding: '12px 16px' },
  infoBox: {
    background: 'var(--bg3)',
    border: '1px solid var(--border)',
    borderRadius: 8,
    padding: '10px 12px',
  },
  infoLabel: {
    fontSize: 10,
    color: 'var(--text3)',
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
  },
  infoValue: {
    fontFamily: 'Syne',
    fontWeight: 700,
    fontSize: 13,
    color: 'var(--text)',
    marginBottom: 6,
  },
  coverageValue: {
    fontFamily: 'Syne',
    fontWeight: 700,
    fontSize: 22,
    color: '#e94560',
    marginBottom: 6,
    lineHeight: 1,
  },
  infoFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: 10,
    color: 'var(--text3)',
    marginTop: 3,
  },
  barWrap: {
    background: 'rgba(255,255,255,0.06)',
    borderRadius: 100,
    height: 5,
    overflow: 'hidden',
  },
  barFill: {
    height: '100%',
    borderRadius: 100,
    transition: 'width 0.5s ease',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 8,
    marginTop: 10,
  },
  tile: {
    background: 'var(--bg3)',
    border: '1px solid var(--border)',
    borderRadius: 8,
    padding: '8px 10px',
  },
  tileLabel: {
    fontSize: 10,
    color: 'var(--text3)',
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
    marginBottom: 3,
  },
  tileVal: {
    fontSize: 18,
    fontWeight: 800,
    fontFamily: 'Syne',
    lineHeight: 1.1,
  },
  tileSub: {
    fontSize: 10,
    color: 'var(--text3)',
    marginTop: 2,
  },
};

export default function Products() {
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [queueFilter, setQueueFilter] = useState('all');
  const [sortBy, setSortBy] = useState('first_seen');
  const [timeRange, setTimeRange] = useState('all');
  const [isAdmin, setIsAdmin] = useState(false);
  const [showAllPV, setShowAllPV] = useState(false);
  const [pvFilter, setPvFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [previewImage, setPreviewImage] = useState(null);
  const [refreshingAll, setRefreshingAll] = useState(false);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const statsPanelRef = useRef(null);
  const searchTimer = useRef(null);
  const requestIdRef = useRef(0);
  const LIMIT = 50;

  const fetchProducts = useCallback(
    async (
      s = search,
      q = queueFilter,
      p = 1,
      append = false,
      nextShowAllPV = showAllPV,
      nextSortBy = sortBy,
      nextPvFilter = pvFilter,
      nextTimeRange = timeRange
    ) => {
      const requestId = ++requestIdRef.current;
      setLoading(true);
      

      try {
        const params = new URLSearchParams({
          limit: LIMIT,
          offset: (p - 1) * LIMIT,
          sort: nextSortBy,
          ...(s && { search: s }),
          ...(q !== 'all' && { queue: q }),
          ...(nextTimeRange !== 'all' && { timeRange: nextTimeRange }),
          ...(nextShowAllPV && { showAllPV: 'true' }),
          ...(q === 'potluck' && !nextShowAllPV && nextPvFilter?.startsWith('acc-') && { pvAccount: nextPvFilter.replace('acc-', '') }),
          ...(q === 'potluck' && !nextShowAllPV && nextPvFilter === 'unknown' && { pvUnknown: 'true' }),
        });

        const res = await fetch(`${API}/products?${params}`, {
          headers: { Authorization: `Bearer ${token()}` },
        });

        const data = await res.json();
	if (requestId !== requestIdRef.current) return;
        const items = data.products || [];

        setTotal(data.total || 0);
        setHasMore(items.length === LIMIT);
        setProducts((prev) => (append ? [...prev, ...items] : items));

        if (data.isAdmin !== undefined) setIsAdmin(data.isAdmin);
        if (!append) setLastUpdatedAt(new Date());
      } catch (e) {
        console.error(e);
      }

      setLoading(false);
    },
    [search, queueFilter, showAllPV, sortBy, pvFilter, timeRange]
  );

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const handleSearch = (val) => {
    setSearch(val);
    clearTimeout(searchTimer.current);

    searchTimer.current = setTimeout(() => {
      setPage(1);
      fetchProducts(val, queueFilter, 1, false, showAllPV, sortBy, pvFilter, timeRange);
    }, 400);
  };

  const handleQueueFilter = (q) => {
    setQueueFilter(q);
    setPvFilter('all');
    setPage(1);
    fetchProducts(search, q, 1, false, showAllPV, sortBy, 'all', timeRange);
  };

  const handleSortChange = (value) => {
    setSortBy(value);
    setPage(1);
    fetchProducts(search, queueFilter, 1, false, showAllPV, value, pvFilter, timeRange);
  };

  const handleTimeRangeChange = (value) => {
    setTimeRange(value);
    setPage(1);
    fetchProducts(search, queueFilter, 1, false, showAllPV, sortBy, pvFilter, value);
  };

  const loadMore = () => {
    const next = page + 1;
    setPage(next);
    fetchProducts(search, queueFilter, next, true, showAllPV, sortBy, pvFilter, timeRange);
  };

  const handleRefreshAll = async () => {
    if (refreshingAll) return;
    setRefreshingAll(true);
    setPage(1);

    try {
      await Promise.all([
        fetchProducts(search, queueFilter, 1, false, showAllPV, sortBy, pvFilter, timeRange),
        statsPanelRef.current?.refresh?.() || Promise.resolve(),
      ]);
      setLastUpdatedAt(new Date());
    } finally {
      setRefreshingAll(false);
    }
  };

  const formatLastUpdated = (d) => {
    if (!d) return null;
    return d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const formatDate = (d) => {
    if (!d) return '-';

    const date = new Date(d);

    return (
      date.toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
      }) +
      ' ' +
      date.toLocaleTimeString('de-DE', {
        hour: '2-digit',
        minute: '2-digit',
      })
    );
  };

const formatPrice = (value) => {
  if (value === null || value === undefined || value === '') return '—';

  const num = Number(value);
  if (!Number.isFinite(num)) return '—';

  return num.toLocaleString('de-DE', {
    style: 'currency',
    currency: 'EUR',
  });
};

  const amazonUrl = (asin) => `https://www.amazon.de/dp/${asin}`;

  const vineUrl = (product) => {
    if (product.search_url) return product.search_url;
    if (!product.product_title) return 'https://www.amazon.de/vine/vine-items';

    const words = product.product_title.split(' ');
    let s = '';

    for (const word of words) {
      const next = s ? `${s} ${word}` : word;
      if (next.length > 50) break;
      s = next;
    }

    return `https://www.amazon.de/vine/vine-items?search=${encodeURIComponent(s)}`;
  };

  return (
    <div style={styles.page}>
      <style>{`@keyframes vineSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>📦 Alle Artikel</h1>
          <div style={styles.subtitle}>
            {total.toLocaleString()} Artikel in der Datenbank
            {timeRange === '24h' && <span style={styles.lastUpdated}> · nur letzte 24h</span>}
            {lastUpdatedAt && (
              <span style={styles.lastUpdated}> · zuletzt aktualisiert {formatLastUpdated(lastUpdatedAt)}</span>
            )}
          </div>
        </div>

        <button
          style={{
            ...styles.refreshBtn,
            opacity: refreshingAll ? 0.75 : 1,
            cursor: refreshingAll ? 'wait' : 'pointer',
          }}
          onClick={handleRefreshAll}
          disabled={refreshingAll}
          title="Produkte und Scanner-Statistiken aktualisieren"
        >
          <RefreshCw size={14} style={{ animation: refreshingAll ? 'vineSpin 0.8s linear infinite' : 'none' }} />
          {refreshingAll ? 'Aktualisiert...' : 'Aktualisieren'}
        </button>
      </div>

      <ScannerStatsPanel ref={statsPanelRef} />

      <div style={styles.toolbar}>
        <div style={styles.searchWrap}>
          <Search size={14} style={{ color: '#666', flexShrink: 0 }} />
          <input
            style={styles.searchInput}
            placeholder="Produktname, Brand oder ASIN suchen..."
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
          />
        </div>

        <div style={styles.filterRow}>
          <div style={styles.sortGroup}>
            <button
              style={{
                ...styles.filterBtn,
                background: sortBy === 'first_seen' ? 'var(--accent)' : 'var(--bg2)',
                color: sortBy === 'first_seen' ? 'white' : 'var(--text2)',
                borderColor: sortBy === 'first_seen' ? 'var(--accent)' : 'var(--border)',
              }}
              onClick={() => handleSortChange('first_seen')}
            >
              Neu gefunden
            </button>

            <button
              style={{
                ...styles.filterBtn,
                background: sortBy === 'last_seen' ? 'var(--accent)' : 'var(--bg2)',
                color: sortBy === 'last_seen' ? 'white' : 'var(--text2)',
                borderColor: sortBy === 'last_seen' ? 'var(--accent)' : 'var(--border)',
              }}
              onClick={() => handleSortChange('last_seen')}
            >
              Zuletzt gesehen
            </button>

            <button
              style={{
                ...styles.filterBtn,
                background: sortBy === 'price_desc' ? 'var(--accent)' : 'var(--bg2)',
                color: sortBy === 'price_desc' ? 'white' : 'var(--text2)',
                borderColor: sortBy === 'price_desc' ? 'var(--accent)' : 'var(--border)',
              }}
              onClick={() => handleSortChange('price_desc')}
              title="Höchster Steuerwert zuerst"
            >
              Preis ↓
            </button>

            <button
              style={{
                ...styles.filterBtn,
                background: sortBy === 'price_asc' ? 'var(--accent)' : 'var(--bg2)',
                color: sortBy === 'price_asc' ? 'white' : 'var(--text2)',
                borderColor: sortBy === 'price_asc' ? 'var(--accent)' : 'var(--border)',
              }}
              onClick={() => handleSortChange('price_asc')}
              title="Niedrigster Steuerwert zuerst"
            >
              Preis ↑
            </button>
          </div>

          <div style={styles.sortGroup}>
            <button
              style={{
                ...styles.filterBtn,
                background: timeRange === 'all' ? 'var(--accent)' : 'var(--bg2)',
                color: timeRange === 'all' ? 'white' : 'var(--text2)',
                borderColor: timeRange === 'all' ? 'var(--accent)' : 'var(--border)',
              }}
              onClick={() => handleTimeRangeChange('all')}
              title="Alle gespeicherten Artikel anzeigen"
            >
              Alle Zeiten
            </button>

            <button
              style={{
                ...styles.filterBtn,
                background: timeRange === '24h' ? 'var(--accent)' : 'var(--bg2)',
                color: timeRange === '24h' ? 'white' : 'var(--text2)',
                borderColor: timeRange === '24h' ? 'var(--accent)' : 'var(--border)',
              }}
              onClick={() => handleTimeRangeChange('24h')}
              title="Nur Artikel anzeigen, die in den letzten 24 Stunden gesehen wurden"
            >
              Letzte 24h
            </button>
          </div>

          {isAdmin && (
            <button
              onClick={() => {
                const nextValue = !showAllPV;
                setShowAllPV(nextValue);
                setPvFilter('all');
                setPage(1);
                fetchProducts(search, queueFilter, 1, false, nextValue, sortBy, 'all', timeRange);
              }}
              style={{
                ...styles.filterBtn,
                background: showAllPV ? '#7c3aed' : 'var(--bg2)',
                color: showAllPV ? 'white' : 'var(--text3)',
                borderColor: showAllPV ? '#7c3aed' : 'var(--border)',
                marginLeft: 'auto',
              }}
            >
              👁 PV Global
            </button>
          )}

          {['all', 'potluck', 'all_items'].map((q) => (
            <button
              key={q}
              style={{
                ...styles.filterBtn,
                background: queueFilter === q ? 'var(--accent)' : 'var(--bg2)',
                color: queueFilter === q ? 'white' : 'var(--text2)',
                borderColor: queueFilter === q ? 'var(--accent)' : 'var(--border)',
              }}
              onClick={() => handleQueueFilter(q)}
            >
              {q === 'all' ? 'Alle' : QUEUE_LABELS[q]?.label || q}
            </button>
          ))}

          {queueFilter === 'potluck' && !showAllPV && (
            <div style={styles.pvFilterWrap}>
              {[{ key: 'all', label: 'PV alle' }, ...Array.from({ length: 11 }, (_, i) => ({ key: `acc-${i + 1}`, label: `PV ${i + 1}` })), { key: 'unknown', label: 'PV unbekannt' }].map((item) => (
                <button
                  key={item.key}
                  style={{
                    ...styles.filterBtn,
                    padding: '6px 10px',
                    background: pvFilter === item.key ? '#1b5e20' : 'var(--bg2)',
                    color: pvFilter === item.key ? '#a5d6a7' : 'var(--text3)',
                    borderColor: pvFilter === item.key ? '#2e7d32' : 'var(--border)',
                  }}
                  onClick={() => {
                    setPvFilter(item.key);
                    setPage(1);
                    fetchProducts(search, queueFilter, 1, false, showAllPV, sortBy, item.key, timeRange);
                  }}
                >
                  {item.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div style={styles.tableWrap}>
        <table style={styles.table}>
          <thead>
            <tr>
              {['ASIN', 'Produktname', 'Preis', 'Queue', 'Zuerst gesehen', 'Zuletzt gesehen', 'Links'].map((h) => (
                <th key={h} style={styles.th}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {products.map((p) => (
              <tr key={p.id} style={styles.tr}>
                <td style={styles.td}>
                  <span style={styles.asinBadge}>{p.asin}</span>
                </td>

                <td style={{ ...styles.td, maxWidth: 460 }}>
                  <div style={styles.productCell}>
                    {(p.image_url || p.imageUrl) && (
  			<img
    			 src={p.image_url || p.imageUrl}
    			 alt=""
    			 style={styles.productImg}
    			 onClick={() => setPreviewImage(p.image_url || p.imageUrl)}
  			/>
			)}                  
		        <div style={styles.productName} title={p.product_title}>
                      {p.product_title || <span style={{ color: '#555' }}>—</span>}
                    </div>
                  </div>
                </td>

		<td style={styles.td}>
  		  <span style={styles.priceBadge}>
   		    {formatPrice(p.tax_value ?? p.taxValue)}
  		  </span>
		</td>


                <td style={styles.td}>
                  <QueueBadge queue={p.queue} product={p} />
                </td>

                <td style={styles.td}>
                  <span style={styles.dateText}>
                    <Clock size={11} style={{ marginRight: 4, opacity: 0.5 }} />
                    {formatDate(p.first_seen)}
                  </span>
                </td>

                <td style={styles.td}>
                  <span style={styles.dateText}>
                    <Eye size={11} style={{ marginRight: 4, opacity: 0.5 }} />
                    {formatDate(p.last_seen)}
                  </span>
                </td>

                <td style={styles.td}>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <a href={amazonUrl(p.asin)} target="_blank" rel="noreferrer" style={styles.linkBtn}>
                      <ExternalLink size={12} /> Amazon
                    </a>

                    <a
                      href={vineUrl(p)}
                      target="_blank"
                      rel="noreferrer"
                      style={{ ...styles.linkBtn, background: '#1b3a5e', color: '#90caf9' }}
                    >
                      🍇 Vine
                    </a>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {!loading && products.length === 0 && (
          <div style={styles.empty}>
            <Package size={32} style={{ color: '#444', marginBottom: 8 }} />
            <div>Keine Artikel gefunden</div>
          </div>
        )}

        {loading && products.length === 0 && (
          <div style={styles.empty}>⏳ Lädt...</div>
        )}

        {hasMore && !loading && products.length > 0 && (
          <div style={{ padding: 16, textAlign: 'center' }}>
            <button style={styles.loadMoreBtn} onClick={loadMore}>
              Weitere laden ({products.length} / {total})
            </button>
          </div>
        )}

        {loading && products.length > 0 && (
          <div style={{ padding: 12, textAlign: 'center', color: '#666', fontSize: 12 }}>
            ⏳ Lädt...
          </div>
        )}

        {previewImage && (
          <div style={styles.imageOverlay} onClick={() => setPreviewImage(null)}>
            <img src={previewImage} alt="" style={styles.previewImg} />
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  page: {
    padding: 24,
    background: 'var(--bg)',
    minHeight: '100vh',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 20,
  },
  title: {
    fontSize: 22,
    fontWeight: 'bold',
    color: 'var(--text)',
    fontFamily: 'Syne',
  },
  subtitle: {
    fontSize: 13,
    color: 'var(--text3)',
    marginTop: 2,
  },
  lastUpdated: {
    color: 'var(--text3)',
    opacity: 0.8,
  },
  refreshBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '8px 14px',
    background: 'var(--bg2)',
    border: '1px solid var(--border)',
    borderRadius: 8,
    color: 'var(--text2)',
    cursor: 'pointer',
    fontSize: 13,
  },
  toolbar: {
    display: 'flex',
    gap: 12,
    marginBottom: 16,
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  searchWrap: {
    flex: 1,
    minWidth: 200,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    background: 'var(--bg2)',
    border: '1px solid var(--border)',
    borderRadius: 8,
    padding: '8px 12px',
  },

  priceBadge: {
    background: 'rgba(34,197,94,0.12)',
    color: '#86efac',
    padding: '3px 8px',
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 700,
    whiteSpace: 'nowrap',
  },

  searchInput: {
    flex: 1,
    background: 'none',
    border: 'none',
    outline: 'none',
    color: 'var(--text)',
    fontSize: 13,
  },
  filterRow: {
    display: 'flex',
    gap: 6,
    flexWrap: 'wrap',
  },
  sortGroup: {
    display: 'flex',
    gap: 6,
  },
  pvFilterWrap: {
    display: 'flex',
    gap: 6,
    flexWrap: 'wrap',
    width: '100%',
    marginTop: 4,
  },
  filterBtn: {
    padding: '7px 14px',
    border: '1px solid',
    borderRadius: 8,
    cursor: 'pointer',
    fontSize: 12,
    fontWeight: 600,
    transition: 'all 0.15s',
  },
  tableWrap: {
    background: 'var(--bg2)',
    borderRadius: 10,
    border: '1px solid var(--border)',
    overflowX: 'auto',
    overflowY: 'hidden',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    minWidth: 720,
  },
  th: {
    padding: '11px 16px',
    textAlign: 'left',
    background: 'var(--bg3)',
    color: 'var(--text3)',
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    borderBottom: '1px solid var(--border)',
  },
  tr: {
    borderBottom: '1px solid var(--border)',
  },
  td: {
    padding: '11px 16px',
    fontSize: 13,
    color: 'var(--text)',
    verticalAlign: 'middle',
  },
  asinBadge: {
    background: 'var(--bg3)',
    color: 'var(--text2)',
    padding: '3px 8px',
    borderRadius: 5,
    fontSize: 11,
    fontFamily: 'monospace',
    fontWeight: 600,
  },
  productCell: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    minWidth: 0,
  },
  productImg: {
    width: 42,
    height: 42,
    objectFit: 'contain',
    background: 'white',
    borderRadius: 6,
    border: '1px solid var(--border)',
    flexShrink: 0,
    cursor: 'zoom-in',
  },
  productName: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'normal',
    maxWidth: 390,
    fontSize: 13,
  },
  dateText: {
    display: 'flex',
    alignItems: 'center',
    fontSize: 12,
    color: 'var(--text3)',
  },
  linkBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '3px 8px',
    background: 'var(--bg3)',
    color: 'var(--text2)',
    borderRadius: 5,
    fontSize: 11,
    textDecoration: 'none',
    border: '1px solid var(--border)',
  },
  empty: {
    padding: 48,
    textAlign: 'center',
    color: 'var(--text3)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  loadMoreBtn: {
    padding: '8px 20px',
    background: 'var(--bg3)',
    border: '1px solid var(--border)',
    borderRadius: 8,
    color: 'var(--text2)',
    cursor: 'pointer',
    fontSize: 13,
  },
  imageOverlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.75)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    cursor: 'zoom-out',
  },
  previewImg: {
    maxWidth: '80vw',
    maxHeight: '80vh',
    objectFit: 'contain',
    background: 'white',
    borderRadius: 12,
    padding: 12,
  },
};